
import React from 'react';
import { Move, ListTree, FileInput, Bot, ChevronRight, Check, CalendarClock, Share, Workflow, Sparkles } from 'lucide-react';
import CTAButton from '../CTAButton';
import { SectionContainer } from '../section/SectionContainer';
import FeatureVideo from './FeatureVideo';

const FlowBuilderSection = () => {
  const features = [{
    icon: <Move className="h-5 w-5" />,
    title: "Drag-and-Drop Flow Builder",
    description: "Easily design structured pathways without coding."
  }, {
    icon: <ListTree className="h-5 w-5" />,
    title: "Guided Content Delivery",
    description: "Provide users with specific information step by step."
  }, {
    icon: <FileInput className="h-5 w-5" />,
    title: "Conversational Data Collection",
    description: "Gather responses in a structured, interactive way."
  }, {
    icon: <Bot className="h-5 w-5" />,
    title: "AI & Chatbot Integration",
    description: "Mix AI-powered conversations with structured workflows."
  }];
  
  const useCases = [{
    icon: <Share className="h-7 w-7" />,
    title: "Content Delivery & Guided Workflows",
    description: "Walk users through structured information step by step, ensuring they get exactly what they need.",
    points: [
      "Provide structured responses for FAQs or learning modules.",
      "Guide customers through troubleshooting steps before escalating to support.",
      "Deliver personalized content based on user selections."
    ]
  }, {
    icon: <CalendarClock className="h-7 w-7" />,
    title: "Conversational Appointment Scheduling",
    description: "Allow users to schedule meetings in a structured, conversational experience.",
    points: [
      "Walk users through appointment options and availability.",
      "Sync bookings with external calendar tools via webhooks.",
      "Send confirmations and reminders automatically."
    ]
  }, {
    icon: <FileInput className="h-7 w-7" />,
    title: "Data Collection & Interactive Forms",
    description: "Capture user inputs in an engaging, conversational way—without requiring a chatbot.",
    points: [
      "Collect structured data without a traditional form layout.",
      "Guide users through surveys, applications, or structured workflows.",
      "Trigger webhook-based actions based on responses."
    ]
  }];

  return (
    <SectionContainer containerWidth="wide" className="relative z-10">
      {/* Header Section */}
      <div className="max-w-6xl mx-auto mb-16 md:mb-20 px-4">
        <div className="relative">
          {/* Background decorative element */}
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-accent/30 rounded-full blur-3xl -z-10"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#003a70]/10 rounded-full blur-3xl -z-10"></div>
          
          {/* Content wrapper with gradient border */}
          <div className="relative border border-gradient-to-r from-accent to-[#003a70]/20 rounded-2xl bg-white/70 backdrop-blur-sm p-8 md:p-10 shadow-xl">
            {/* Badge with animated sparkle effect */}
            <div className="flex justify-center mb-6">
              <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-accent to-accent/60 text-[#003a70] text-sm font-medium shadow-sm transform hover:scale-105 transition-all duration-300">
                <Sparkles className="h-4 w-4 animate-pulse" />
                <span className="relative z-10 font-semibold tracking-wide">FLOW BUILDER</span>
              </div>
            </div>
            
            {/* Two-part heading with underline accent */}
            <h2 className="text-center">
              <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-[#003a70] mb-3 leading-tight">
                Design Structured Interactions
              </span>
              <span className="relative inline-block text-2xl md:text-3xl lg:text-4xl font-bold text-[#003a70]/80">
                Without Code
                <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-accent rounded-full transform -skew-x-6"></span>
              </span>
            </h2>
            
            {/* Enhanced description */}
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mt-8 text-center leading-relaxed">
              Guide users through curated pathways for content delivery, data collection, and appointment scheduling—integrate AI where it makes sense.
            </p>
          </div>
        </div>
      </div>

      {/* Video Section */}
      <FeatureVideo 
        section="flow-builder"
      />

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {features.map((feature, index) => (
          <div 
            key={index} 
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 hover:border-accent"
          >
            <div className="bg-accent p-3 rounded-lg inline-block mb-4">
              <div className="text-[#003a70]">
                {feature.icon}
              </div>
            </div>
            <h3 className="text-lg font-semibold text-[#003a70] mb-2">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* Use Cases Section */}
      <div className="mb-12">
        <h3 className="text-xl md:text-2xl font-semibold text-[#003a70] text-center mb-10">
          How Businesses Use Flow Builder
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-accent group"
            >
              <div className="bg-accent p-3 rounded-lg inline-block mb-4 group-hover:bg-[#003a70] transition-colors duration-300">
                <div className="text-[#003a70] group-hover:text-white transition-colors duration-300">
                  {useCase.icon}
                </div>
              </div>
              <h4 className="text-lg font-semibold text-[#003a70] mb-3">{useCase.title}</h4>
              <p className="text-gray-600 mb-5">{useCase.description}</p>
              <ul className="space-y-2">
                {useCase.points.map((point, i) => (
                  <li key={i} className="flex items-start">
                    <Check className="h-5 w-5 text-[#003a70] mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <CTAButton 
          variant="primary" 
          className="group text-lg px-8 py-4"
        >
          Try Flow Builder Now
          <ChevronRight className="inline-block ml-1 group-hover:translate-x-1 transition-transform" />
        </CTAButton>
      </div>
    </SectionContainer>
  );
};

export default FlowBuilderSection;
